import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  getPendingAwaiting, getPendingJava, getPendingPhp, getPendingUnpaid, rejectUnpaidDeposit, updateAwaitingDepositStatus,
} from '../api/client';
import PendingStatusModal from '../components/PendingStatusModal';
import BulkPendingStatusModal, {
  awaitingModalRecord,
  isPendingRowUpdatable,
  pendingRowKey,
} from '../components/BulkPendingStatusModal';
import { downloadCsv, fetchAllPages, fileTimestamp, toCsv } from '../utils/csvExport';

// 50 rows per page on every Pending card (2026-10-29, ops request; was 10).
// The API caps per_page at 100.
const PER_PAGE = 50;
const DEPOSIT_PER_PAGE = PER_PAGE;

// Search box for the deposit cards. Debounced; the search runs server-side
// over every row, not just the page shown.
function CardSearch({ value, onChange, placeholder }) {
  const [text, setText] = useState(value);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return undefined;
    }
    const t = setTimeout(() => onChange(text), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text]);
  return (
    <div className="input-group input-group-sm mb-2" style={{ maxWidth: '28rem' }}>
      <input
        type="search"
        className="form-control"
        placeholder={placeholder}
        aria-label={placeholder}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {text && (
        <button type="button" className="btn btn-outline-secondary" onClick={() => setText('')}>
          Clear
        </button>
      )}
    </div>
  );
}

// Java Pending's Redis key shape was never confirmed against a live cluster
// (unlike Merchants, no sample record was given for this one) - rather than
// guess at field names, columns are derived dynamically from whatever keys
// the first returned record actually has. Capped so an unexpectedly wide
// record doesn't blow out the table.
const MAX_DYNAMIC_COLUMNS = 8;

// Preferred left-to-right column order for the Java Pending table
// (2026-08-20, user request - the raw Redis key order put merchant_order_id
// first and buried datetime_created/instruction_id further right). This does
// NOT invent or drop columns: they're still derived from whatever the record
// actually has, this only pulls these keys to the front, in this order, when
// they're present. Everything else keeps its original relative order behind
// them. Extend the list to promote more columns.
const JAVA_COLUMN_ORDER = ['datetime_created', 'instruction_id', 'merchant_order_id'];

// Compared on a normalized form (lowercase, non-alphanumerics stripped) so a
// preference written as datetime_created still matches a record whose key
// turns out to be datetimeCreated - the Java Pending Redis record's casing
// was never confirmed against a live cluster, same caveat as
// PendingStatusModal's javaIdentifier fallbacks.
function normalizeColumnName(key) {
  return String(key).toLowerCase().replace(/[^a-z0-9]/g, '');
}

function orderJavaColumns(keys) {
  const remaining = [...keys];
  const ordered = [];

  JAVA_COLUMN_ORDER.forEach((preferred) => {
    const i = remaining.findIndex((k) => normalizeColumnName(k) === normalizeColumnName(preferred));
    if (i !== -1) {
      ordered.push(remaining.splice(i, 1)[0]);
    }
  });

  return [...ordered, ...remaining];
}

// Columns exempt from the 14rem truncate-with-ellipsis cap every other
// dynamic column gets (2026-08-20, user request: instruction_id was showing
// as "20260814PPBIPHM2XXXB01000…" with the tail cut off).
//
// The cap exists because MAX_DYNAMIC_COLUMNS derives columns from an
// unconfirmed record shape, so one unexpectedly long field shouldn't be able
// to blow the table out - but instruction_id is a known, fixed-length
// identifier (~35 chars) that operators read and compare in full, and a
// value you have to hover to read is a value you can't scan down a column.
// The table already sits in a .table-responsive wrapper with a horizontal
// scrollbar, so the extra width costs scroll distance in a half-width card,
// not a broken layout.
//
// Matched via normalizeColumnName() so this list stays casing-agnostic the
// same way JAVA_COLUMN_ORDER above is.
const JAVA_UNTRUNCATED_COLUMNS = ['instruction_id'];

function isUntruncatedColumn(key) {
  return JAVA_UNTRUNCATED_COLUMNS.some((c) => normalizeColumnName(c) === normalizeColumnName(key));
}

// Rough px width of one page-number button (btn-sm, 1-2 digits) plus its
// gap-1 spacing - used only to estimate how many buttons fit in the
// available width, not for exact layout, so an approximate constant is
// fine here. Deliberately on the small side (a real btn-sm 1-2 digit
// button plus gap-1 is closer to ~34px) so the estimate errs toward
// showing a few more numbers rather than leaving visible unused space.
const PAGE_BUTTON_SLOT_WIDTH = 34;
const MIN_VISIBLE_PAGE_NUMBERS = 3;
// Shown before the first real width measurement lands, and as a floor if
// that measurement is ever unavailable (e.g. no ResizeObserver support) -
// generous enough on its own to look reasonable on a typical desktop
// column width without waiting on JS measurement at all.
const DEFAULT_VISIBLE_PAGE_NUMBERS = 13;

// Builds a compact page-number list with ellipsis gaps (e.g.
// [1, '...', 4, 5, 6, '...', 16]) for a given "neighbors on each side of
// current" radius - always includes page 1 and the last page.
function buildPageList(current, total, delta) {
  const range = [];
  const withDots = [];
  let last;

  for (let i = 1; i <= total; i += 1) {
    if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
      range.push(i);
    }
  }

  range.forEach((i) => {
    if (last !== undefined) {
      if (i - last === 2) {
        withDots.push(last + 1);
      } else if (i - last > 2) {
        withDots.push('...');
      }
    }
    withDots.push(i);
    last = i;
  });

  return withDots;
}

// Grows the neighbor radius outward, one step at a time, for as long as the
// result still fits within `maxVisible` slots - so a wide card shows more
// page numbers (1 2 3 4 5 6 7 8 9 10 11 ...) instead of always collapsing
// to the same fixed "first ... current ... last" shape regardless of how
// much horizontal space is actually available.
function getPageNumbers(current, total, maxVisible) {
  let delta = 1;
  let list = buildPageList(current, total, delta);

  while (list.length < maxVisible && list.length < total) {
    const next = buildPageList(current, total, delta + 1);
    if (next.length > maxVisible || next.length === list.length) {
      break;
    }
    delta += 1;
    list = next;
  }

  return list;
}

function cellValue(value) {
  if (value === null || value === undefined) {
    return '—';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
}

// Selection is kept across pages (2026-10-29), so it is a Map of row key ->
// row rather than a Set of keys: rows ticked on another page are no longer
// in the card's state, so the row itself has to be kept for the bulk modal.
// The header checkbox only ever reflects and toggles the rows on THIS page.
function countSelectedOnPage(records, keySource, selected) {
  let n = 0;
  for (const r of records) {
    if (isPendingRowUpdatable(keySource, r) && selected.has(pendingRowKey(keySource, r))) {
      n += 1;
    }
  }
  return n;
}

// After a page loads, swap in the fresh copy of any row that is selected
// and shown on it, so a later bulk send uses current data.
function refreshSelectedRows(setSelected, keySource, rows) {
  setSelected((prev) => {
    if (prev.size === 0) {
      return prev;
    }
    let next = null;
    for (const r of rows) {
      const k = pendingRowKey(keySource, r);
      if (k && prev.has(k)) {
        next = next || new Map(prev);
        next.set(k, r);
      }
    }
    return next || prev;
  });
}

// Header checkbox for a table's bulk selection. Needs its own component
// only because `indeterminate` (the "some but not all rows selected" dash)
// is a DOM property with no HTML attribute, so React can't set it via JSX -
// it has to be assigned on the node itself.
function SelectAllCheckbox({ checked, indeterminate, disabled, onChange, label }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  return (
    <input
      ref={ref}
      type="checkbox"
      className="form-check-input"
      checked={Boolean(checked)}
      disabled={disabled}
      onChange={onChange}
      aria-label={label}
    />
  );
}

// Per-row selection cell, shared by both tables. stopPropagation matters:
// the whole <tr> is clickable and opens the single-row detail modal, so
// without it ticking a checkbox would also pop that modal open.
function RowSelectCell({ selectable, checked, onToggle, title }) {
  return (
    <td onClick={(e) => e.stopPropagation()}>
      <input
        type="checkbox"
        className="form-check-input"
        checked={checked}
        disabled={!selectable}
        onChange={onToggle}
        aria-label={selectable ? 'Select transaction' : 'Row cannot be bulk updated'}
        title={selectable ? title : 'This row is missing a required identifier field.'}
      />
    </td>
  );
}

function DynamicTable({ records, onRowClick, selected, onToggleRow, onToggleAll }) {
  const columns = useMemo(() => {
    const first = records.find((r) => r && typeof r === 'object');
    if (!first) {
      return [];
    }
    // Reorder BEFORE slicing, so a promoted column can never be the one that
    // falls off the MAX_DYNAMIC_COLUMNS cap.
    return orderJavaColumns(Object.keys(first)).slice(0, MAX_DYNAMIC_COLUMNS);
  }, [records]);

  const selectableCount = useMemo(
    () => records.filter((r) => isPendingRowUpdatable('java', r)).length,
    [records]
  );

  if (columns.length === 0) {
    return <div className="text-muted small">No structured fields found in these records.</div>;
  }

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: '2rem' }}>
              <SelectAllCheckbox
                checked={selectableCount > 0 && countSelectedOnPage(records, 'java', selected) === selectableCount}
                indeterminate={countSelectedOnPage(records, 'java', selected) > 0
                  && countSelectedOnPage(records, 'java', selected) < selectableCount}
                disabled={selectableCount === 0}
                onChange={onToggleAll}
                label="Select all Java pending transactions on this page"
              />
            </th>
            {columns.map((c) => (
              <th key={c} className={isUntruncatedColumn(c) ? 'text-nowrap' : undefined}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {records.map((row, i) => {
            const key = pendingRowKey('java', row);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr
                key={key || i}
                role="button"
                style={{ cursor: 'pointer' }}
                onClick={() => onRowClick?.(row)}
              >
                <RowSelectCell
                  selectable={isPendingRowUpdatable('java', row)}
                  checked={selected.has(key)}
                  onToggle={() => onToggleRow(key, row)}
                  title={key || ''}
                />
                {columns.map((c) => (
                  <td
                    key={c}
                    // text-nowrap, not just "no truncate": without it the
                    // long identifier would wrap onto two lines inside the
                    // half-width card instead of widening the column, which
                    // is no easier to read than the ellipsis was.
                    className={isUntruncatedColumn(c) ? 'text-nowrap' : 'text-truncate'}
                    style={isUntruncatedColumn(c) ? undefined : { maxWidth: '14rem' }}
                    title={cellValue(row?.[c])}
                  >
                    {cellValue(row?.[c])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function PhpPendingTable({ records, onRowClick, selected, onToggleRow, onToggleAll }) {
  const selectableCount = useMemo(
    () => records.filter((r) => isPendingRowUpdatable('php', r)).length,
    [records]
  );

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: '2rem' }}>
              <SelectAllCheckbox
                checked={selectableCount > 0 && countSelectedOnPage(records, 'php', selected) === selectableCount}
                indeterminate={countSelectedOnPage(records, 'php', selected) > 0
                  && countSelectedOnPage(records, 'php', selected) < selectableCount}
                disabled={selectableCount === 0}
                onChange={onToggleAll}
                label="Select all PHP pending transactions on this page"
              />
            </th>
            <th>Datetime</th>
            <th>Reference no</th>
            <th>Client reference no</th>
            <th>Is merchant</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {records.map((row, i) => {
            const key = pendingRowKey('php', row);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={key || i} role="button" style={{ cursor: 'pointer' }} onClick={() => onRowClick?.(row)}>
                <RowSelectCell
                  selectable={isPendingRowUpdatable('php', row)}
                  checked={selected.has(key)}
                  onToggle={() => onToggleRow(key, row)}
                  title={key || ''}
                />
                <td className="text-nowrap">{cellValue(row.transaction_datetime)}</td>
                <td>{cellValue(row.reference_no)}</td>
                <td>{cellValue(row.client_reference_no)}</td>
                <td>{cellValue(row.is_merchant)}</td>
                <td>
                  <span className="badge text-bg-secondary">{cellValue(row.status)}</span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function formatAge(seconds) {
  if (seconds === null || seconds === undefined || Number.isNaN(Number(seconds))) {
    return '—';
  }
  const s = Math.max(Math.floor(Number(seconds)), 0);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ${m % 60}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

function formatAmount(value) {
  const n = Number(value);
  if (value === null || value === undefined || value === '' || Number.isNaN(n)) {
    return cellValue(value);
  }
  return n.toLocaleString('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function ManualUpdateBadge({ marker }) {
  if (!marker) {
    return <span className="text-muted small">—</span>;
  }
  const rejected = marker.status === 'rejected';
  const title = [
    `Sent ${marker.status} to Java`,
    marker.updated_by ? `by ${marker.updated_by}` : null,
    marker.updated_at ? `at ${marker.updated_at}` : null,
    marker.error_message ? `(${marker.error_message})` : null,
  ]
    .filter(Boolean)
    .join(' ');
  return (
    <span className={`badge ${rejected ? 'text-bg-danger' : 'text-bg-success'}`} title={title}>
      Sent: {marker.status}
    </span>
  );
}

function AwaitingNotice({ meta }) {
  if (!meta || Object.keys(meta).length === 0) {
    return null;
  }
  const hidden = [];
  if (meta.inFlight) {
    hidden.push(`${meta.inFlight} still in flight (under ${meta.minAge ?? 60}s old)`);
  }
  if (meta.completed) {
    hidden.push(`${meta.completed} already responded`);
  }
  return (
    <>
      {!meta.scanComplete && (
        <div className="alert alert-warning py-2 small">
          The Redis scan hit its time limit before covering every key, so this list may be incomplete. Refresh to scan again.
        </div>
      )}
      {meta.capped && (
        <div className="alert alert-warning py-2 small">
          More candidates than the 5,000 this list holds - showing the first 5,000 found.
        </div>
      )}
      {meta.historyError && (
        <div className="alert alert-warning py-2 small">
          Could not check older rows against the transaction history, so rows marked &ldquo;unverified&rdquo; may
          already be completed. ({meta.historyError})
        </div>
      )}
      {hidden.length > 0 && <p className="text-muted small mb-2">Not listed: {hidden.join('; ')}.</p>}
    </>
  );
}

function AwaitingTable({ records, onRowClick, selected, onToggleRow, onToggleAll }) {
  const selectableCount = useMemo(
    () => records.filter((r) => isPendingRowUpdatable('java', r)).length,
    [records]
  );

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: '2rem' }}>
              <SelectAllCheckbox
                checked={selectableCount > 0 && countSelectedOnPage(records, 'java', selected) === selectableCount}
                indeterminate={countSelectedOnPage(records, 'java', selected) > 0
                  && countSelectedOnPage(records, 'java', selected) < selectableCount}
                disabled={selectableCount === 0}
                onChange={onToggleAll}
                label="Select all transactions awaiting a Bancnet response on this page"
              />
            </th>
            <th className="text-nowrap" title="Static QR has no order from Java; its Leg 3 time is shown instead">
              Order created / Leg 3
            </th>
            <th title="Since Leg 3 when known, otherwise since the order was created">Waiting</th>
            <th className="text-nowrap">Instruction ID</th>
            <th className="text-nowrap">Merchant order ID</th>
            <th>Merchant</th>
            <th className="text-end">Amount</th>
            <th>Type</th>
            <th className="text-nowrap">Sent to Java</th>
          </tr>
        </thead>
        <tbody>
          {records.map((row, i) => {
            const key = pendingRowKey('java', row);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={key || i} role="button" style={{ cursor: 'pointer' }} onClick={() => onRowClick?.(row)}>
                <RowSelectCell
                  selectable={isPendingRowUpdatable('java', row)}
                  checked={selected.has(key)}
                  onToggle={() => onToggleRow(key, row)}
                  title={key || ''}
                />
                <td className="text-nowrap">
                  {cellValue(row.created_at)}
                  {row.deposit_type === 'static' && row.created_at ? <span className="text-muted small"> · Leg 3</span> : null}
                </td>
                <td className="text-nowrap">
                  {formatAge(row.leg3_age_seconds ?? row.age_seconds)}
                  {row.history_checked === false && (
                    <span
                      className="badge text-bg-warning ms-1"
                      title="Older than the callback record lifetime and the history check could not run, so this may already be completed."
                    >
                      unverified
                    </span>
                  )}
                </td>
                <td className="text-nowrap">{cellValue(row.instruction_id)}</td>
                <td className="text-nowrap">{cellValue(row.merchant_order_id)}</td>
                <td title={row.merchant_id || ''}>{cellValue(row.merchant_name || row.merchant_id)}</td>
                <td className="text-end text-nowrap">{formatAmount(row.amount)}</td>
                <td className="text-nowrap">
                  {cellValue(row.transaction_type)}
                  {row.deposit_type ? <span className="text-muted small"> · {row.deposit_type}</span> : null}
                </td>
                <td>
                  <ManualUpdateBadge marker={row.manual_update} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Java Pending QP2M rows were accepted at Leg 3 and only await Bancnet's
// payment instruction, so they can only be confirmed as paid.
function isQp2m(record) {
  return String(record?.transaction_type || '').toUpperCase() === 'QP2M';
}

// Static QR deposits on the Awaiting card can only be confirmed as paid.
export function isStaticRecord(record) {
  return (record?.leg3_deposit_type || record?.deposit_type) === 'static';
}

function paidOnlyFor(list, records) {
  const rows = (records || []).filter(Boolean);
  if (list === 'java') return rows.some(isQp2m);
  if (list === 'awaiting') return rows.some(isStaticRecord);
  return false;
}

// The Awaiting / no-Leg-3 scan runs in the background on the server
// (2026-10-14). While it builds, the cards poll; a stale copy is shown with
// a note while a fresh one is made.
const SCAN_RETRY_MS = 4000;
const SCAN_MAX_RETRIES = 45;

function ScanStatus({ meta }) {
  if (meta?.building) {
    return (
      <div className="alert alert-info py-2 small d-flex align-items-center gap-2">
        <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
        Scanning Redis to build this list. It loads automatically when ready.
      </div>
    );
  }
  if (meta?.stale) {
    const at = meta.generatedAt ? new Date(meta.generatedAt).toLocaleTimeString() : null;
    return (
      <p className="text-muted small mb-2">
        Showing the list from {at || 'the last scan'}; a fresh scan is running. Refresh in a moment for the latest.
      </p>
    );
  }
  return null;
}

function UnpaidNotice({ meta }) {
  if (!meta || Object.keys(meta).length === 0) {
    return null;
  }
  const minutes = Math.round((meta.minAgeSeconds || 0) / 60);
  const hours = Math.round(((meta.maxAgeSeconds || 0) / 3600) * 10) / 10;
  return (
    <>
      {!meta.scanComplete && (
        <div className="alert alert-warning py-2 small">
          The Redis scan hit its time limit, so this list may be incomplete. Refresh to scan again.
        </div>
      )}
      <p className="text-muted small mb-2">
        Orders {minutes} min to {hours} h old with no Leg 3.
        {meta.tooRecent ? ` ${meta.tooRecent} newer order${meta.tooRecent === 1 ? '' : 's'} not shown yet.` : ''}
        {meta.tooOld ? ` ${meta.tooOld} older than ${hours} h not shown.` : ''}
        {' '}Check each one in Bancnet before rejecting: a QR that is still valid can still be paid.
      </p>
    </>
  );
}

function UnpaidTable({ records, onRowClick, selected, onToggleRow, onToggleAll }) {
  const selectableCount = useMemo(
    () => records.filter((r) => isPendingRowUpdatable('java', r)).length,
    [records]
  );

  return (
    <div className="table-responsive">
      <table className="table table-sm table-hover align-middle mb-0">
        <thead className="table-light">
          <tr>
            <th style={{ width: '2rem' }}>
              <SelectAllCheckbox
                checked={selectableCount > 0 && countSelectedOnPage(records, 'java', selected) === selectableCount}
                indeterminate={countSelectedOnPage(records, 'java', selected) > 0
                  && countSelectedOnPage(records, 'java', selected) < selectableCount}
                disabled={selectableCount === 0}
                onChange={onToggleAll}
                label="Select all unpaid deposits on this page"
              />
            </th>
            <th className="text-nowrap">Order created</th>
            <th>Waiting</th>
            <th className="text-nowrap">Merchant order ID</th>
            <th>Merchant</th>
            <th className="text-end">Amount</th>
            <th className="text-nowrap">Sent to Java</th>
          </tr>
        </thead>
        <tbody>
          {records.map((row, i) => {
            const key = pendingRowKey('java', row);
            return (
              // eslint-disable-next-line react/no-array-index-key
              <tr key={key || i} role="button" style={{ cursor: 'pointer' }} onClick={() => onRowClick?.(row)}>
                <RowSelectCell
                  selectable={isPendingRowUpdatable('java', row)}
                  checked={selected.has(key)}
                  onToggle={() => onToggleRow(key, row)}
                  title={key || ''}
                />
                <td className="text-nowrap">{cellValue(row.created_at)}</td>
                <td className="text-nowrap">{formatAge(row.age_seconds)}</td>
                <td className="text-nowrap">{cellValue(row.merchant_order_id)}</td>
                <td title={row.merchant_id || ''}>{cellValue(row.merchant_name || row.merchant_id)}</td>
                <td className="text-end text-nowrap">{formatAmount(row.amount)}</td>
                <td>
                  <ManualUpdateBadge marker={row.manual_update} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// Bulk-action strip above a card's table. Only rendered once the card has
// rows; the button stays visible (disabled) with nothing selected so the
// feature is discoverable without having to tick something first.
function BulkBar({ selectedCount, onBulkUpdate, onClearSelection }) {
  return (
    <div className="d-flex align-items-center gap-2 mb-2 flex-wrap">
      <button
        type="button"
        className="btn btn-sm btn-primary"
        onClick={onBulkUpdate}
        disabled={selectedCount === 0}
      >
        Update selected ({selectedCount})
      </button>
      {selectedCount > 0 && (
        <button type="button" className="btn btn-sm btn-link text-decoration-none" onClick={onClearSelection}>
          Clear
        </button>
      )}
      <span className="text-muted small ms-auto">
        Tick rows to update together (ticks are kept when you change page), or click a row for details.
      </span>
    </div>
  );
}

// ---- CSV export (2026-10-26) ----
// Exports the WHOLE list a card is showing (every page, with the card's
// current search applied), not just the rows on screen.

function amountForCsv(value) {
  const n = Number(value);
  return value === null || value === undefined || value === '' || Number.isNaN(n) ? value : n.toFixed(2);
}

const SENT_TO_JAVA_COLUMNS = [
  { header: 'Sent to Java', value: (r) => r.manual_update?.status },
  { header: 'Sent by', value: (r) => r.manual_update?.updated_by },
  { header: 'Sent at', value: (r) => r.manual_update?.updated_at },
  { header: 'Sent code / error', value: (r) => r.manual_update?.error_message },
];

const AWAITING_CSV_COLUMNS = [
  { header: 'Order created / Leg 3', value: (r) => r.created_at },
  { header: 'Waiting', value: (r) => formatAge(r.leg3_age_seconds ?? r.age_seconds) },
  { header: 'Waiting (seconds)', value: (r) => r.leg3_age_seconds ?? r.age_seconds },
  { header: 'Instruction ID', value: (r) => r.instruction_id },
  { header: 'Merchant order ID', value: (r) => r.merchant_order_id },
  { header: 'Merchant ID', value: (r) => r.merchant_id },
  { header: 'Merchant name', value: (r) => r.merchant_name },
  { header: 'Amount', value: (r) => amountForCsv(r.amount) },
  { header: 'Transaction type', value: (r) => r.transaction_type },
  { header: 'Deposit type', value: (r) => r.deposit_type },
  { header: 'History check', value: (r) => (r.history_checked === false ? 'unverified' : '') },
  ...SENT_TO_JAVA_COLUMNS,
];

const UNPAID_CSV_COLUMNS = [
  { header: 'Order created', value: (r) => r.created_at },
  { header: 'Waiting', value: (r) => formatAge(r.age_seconds) },
  { header: 'Waiting (seconds)', value: (r) => r.age_seconds },
  { header: 'Merchant order ID', value: (r) => r.merchant_order_id },
  { header: 'Merchant ID', value: (r) => r.merchant_id },
  { header: 'Merchant name', value: (r) => r.merchant_name },
  { header: 'Amount', value: (r) => amountForCsv(r.amount) },
  ...SENT_TO_JAVA_COLUMNS,
];

const PHP_CSV_COLUMNS = [
  { header: 'Datetime', value: (r) => r.transaction_datetime },
  { header: 'Reference no', value: (r) => r.reference_no },
  { header: 'Client reference no', value: (r) => r.client_reference_no },
  { header: 'Is merchant', value: (r) => r.is_merchant },
  { header: 'Status', value: (r) => r.status },
];

// Java Pending's record shape isn't fixed, so the CSV takes every field seen
// in any row (the on-screen table stops at MAX_DYNAMIC_COLUMNS), in the same
// order the table uses.
function javaCsvColumns(rows) {
  const keys = [];
  const seen = new Set();
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    for (const k of Object.keys(row)) {
      if (!seen.has(k)) {
        seen.add(k);
        keys.push(k);
      }
    }
  }
  return orderJavaColumns(keys).map((k) => ({ header: k, value: (r) => r?.[k] }));
}

function ExportCsvButton({ fetchPage, columns, keySource, fileSlug, query, disabled }) {
  const [state, setState] = useState({ busy: false, progress: null, error: null });
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  async function run() {
    setState({ busy: true, progress: null, error: null });
    try {
      const { rows } = await fetchAllPages(fetchPage, {
        keyOf: (r) => pendingRowKey(keySource, r),
        buildingRetryMs: SCAN_RETRY_MS,
        buildingMaxRetries: SCAN_MAX_RETRIES,
        onProgress: (p) => alive.current && setState((s) => ({ ...s, progress: p })),
      });
      const cols = typeof columns === 'function' ? columns(rows) : columns;
      const suffix = query && query.trim() ? '-filtered' : '';
      downloadCsv(`pending-${fileSlug}${suffix}-${fileTimestamp()}.csv`, toCsv(cols, rows));
      if (alive.current) setState({ busy: false, progress: null, error: null });
    } catch (err) {
      if (alive.current) setState({ busy: false, progress: null, error: err.message || 'Export failed.' });
    }
  }

  const p = state.progress;
  let label = 'Export CSV';
  if (state.busy) {
    if (p?.building) label = 'Waiting for scan…';
    else if (p && p.total > 1) label = `Exporting ${p.done}/${p.total}…`;
    else label = 'Exporting…';
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-outline-secondary"
        onClick={run}
        disabled={disabled || state.busy}
        title={query && query.trim() ? 'Download every row matching the search' : 'Download every row in this list'}
      >
        {label}
      </button>
      {state.error && <span className="text-danger small">Export failed: {state.error}</span>}
    </>
  );
}

function PendingCard({
  title,
  hint,
  colClass = 'col-lg-6',
  notice,
  loading,
  error,
  meta,
  records,
  table,
  bulkBar,
  onRefresh,
  page,
  totalPages,
  total,
  onPrevPage,
  onNextPage,
  onGoToPage,
  search,
  searchActive = false,
  unfilteredTotal,
  exportButton,
}) {
  const pageNumbersRef = useRef(null);
  const [maxVisiblePages, setMaxVisiblePages] = useState(DEFAULT_VISIBLE_PAGE_NUMBERS);

  // Recompute how many page-number buttons fit in the available width
  // whenever the container resizes (window resize, or the card itself
  // changing width e.g. at responsive breakpoints) - this is what makes
  // the page list actually "fill the space" instead of always showing the
  // same fixed handful of numbers regardless of how wide the card is.
  useEffect(() => {
    const el = pageNumbersRef.current;
    if (!el || typeof ResizeObserver === 'undefined') {
      return undefined;
    }

    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect?.width || 0;
      const slots = Math.floor(width / PAGE_BUTTON_SLOT_WIDTH);
      setMaxVisiblePages(Math.max(slots, MIN_VISIBLE_PAGE_NUMBERS));
    });

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div className={colClass}>
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body d-flex flex-column">
          <div className="d-flex align-items-center justify-content-between mb-1">
            <h2 className="h6 mb-0">{title}</h2>
            <div className="d-flex align-items-center gap-2 flex-wrap justify-content-end">
              {exportButton}
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={onRefresh} disabled={loading}>
                {loading ? 'Refreshing…' : 'Refresh'}
              </button>
            </div>
          </div>
          <p className="text-muted small mb-3">{hint}</p>

          {error && <div className="alert alert-danger py-2 small">{error}</div>}
          {!error && notice}
          {search}

          {!error && meta?.type === 'none' && (
            <div className="alert alert-warning py-2 small">
              No data found at the expected key/source ({meta.key || 'n/a'}). Either it doesn&rsquo;t
              exist in this environment yet, or an assumption this page was built on is wrong.
            </div>
          )}

          {loading && records.length === 0 && !error && (
            <div className="d-flex align-items-center gap-2 text-muted py-3">
              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              Loading…
            </div>
          )}

          {!loading && !error && records.length === 0 && meta?.type !== 'none' && (
            <div className="text-muted small py-2">{searchActive ? 'No matches.' : 'Nothing pending right now.'}</div>
          )}

          {records.length > 0 && bulkBar}
          {records.length > 0 && table}

          <div className="mt-auto">
            {!loading && total > 0 && (
              <p className="text-muted small mt-2 mb-2">
                {searchActive && typeof unfilteredTotal === 'number'
                  ? `${total} matching of ${unfilteredTotal} pending`
                  : `${total} pending total`}
                {totalPages > 1 ? ` — page ${page} of ${totalPages}` : ''}.
              </p>
            )}

            {totalPages > 1 && (
              <nav className="d-flex align-items-center justify-content-between w-100 flex-wrap gap-2">
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onPrevPage}
                  disabled={loading || page <= 1}
                >
                  Previous
                </button>
                <div
                  ref={pageNumbersRef}
                  className="d-flex align-items-center justify-content-center flex-grow-1 gap-1 flex-wrap"
                >
                  {getPageNumbers(page, totalPages, maxVisiblePages).map((p, idx) =>
                    p === '...' ? (
                      // eslint-disable-next-line react/no-array-index-key
                      <span key={`dots-${idx}`} className="text-muted small px-1">
                        &hellip;
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => onGoToPage(p)}
                        disabled={loading || p === page}
                      >
                        {p}
                      </button>
                    )
                  )}
                </div>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={onNextPage}
                  disabled={loading || page >= totalPages}
                >
                  Next
                </button>
              </nav>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TransactionPending() {
  const [java, setJava] = useState({ loading: true, error: null, meta: {}, transactions: [], total: 0, page: 1 });
  const [php, setPhp] = useState({ loading: true, error: null, transactions: [], total: 0, page: 1 });
  const [awaiting, setAwaiting] = useState({ loading: true, error: null, meta: {}, transactions: [], total: 0, page: 1 });
  const [awaitingSelected, setAwaitingSelected] = useState(() => new Map());
  const [unpaid, setUnpaid] = useState({ loading: true, error: null, meta: {}, transactions: [], total: 0, page: 1 });
  const [unpaidSelected, setUnpaidSelected] = useState(() => new Map());
  const [awaitingQuery, setAwaitingQuery] = useState('');
  const [unpaidQuery, setUnpaidQuery] = useState('');
  // Latest search term and request number per card, so paging uses the
  // current search and a slow older response can't overwrite a newer one.
  const awaitingQ = useRef('');
  const unpaidQ = useRef('');
  const awaitingSeq = useRef(0);
  const unpaidSeq = useRef(0);
  const awaitingRetry = useRef({ timer: null, count: 0 });
  const unpaidRetry = useRef({ timer: null, count: 0 });
  const [javaQuery, setJavaQuery] = useState('');
  const [phpQuery, setPhpQuery] = useState('');
  const javaQ = useRef('');
  const phpQ = useRef('');
  const javaSeq = useRef(0);
  const phpSeq = useRef(0);
  // `list` is which card the row came from; `source` is which update API
  // it goes to. They differ only for the awaiting card, whose rows are sent
  // to Java through the same endpoint as Java Pending.
  const [modal, setModal] = useState({ show: false, source: null, list: null, record: null });

  // Bulk selection (2026-08-20). One selection per card - they hit different
  // endpoints with different required fields, so a shared selection would
  // never be submittable as a single batch. Since 2026-10-29 it is a Map of
  // row key -> row and survives page changes and searches (see
  // countSelectedOnPage). Refresh and Clear empty it.
  const [javaSelected, setJavaSelected] = useState(() => new Map());
  const [phpSelected, setPhpSelected] = useState(() => new Map());
  const [bulkModal, setBulkModal] = useState({ show: false, source: null, list: null, records: [] });

  const loadJava = useCallback(async (page = 1) => {
    const seq = ++javaSeq.current;
    setJava((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getPendingJava({ page, perPage: PER_PAGE, q: javaQ.current });
      if (seq !== javaSeq.current) return;
      refreshSelectedRows(setJavaSelected, 'java', data.transactions || []);
      setJava({
        loading: false,
        error: null,
        meta: { type: data.type, key: data.key },
        transactions: data.transactions || [],
        total: data.total || 0,
        unfilteredTotal: data.unfiltered_total,
        page: data.page || page,
      });
    } catch (err) {
      if (seq !== javaSeq.current) return;
      setJava((s) => ({ ...s, loading: false, error: err.message || 'Failed to load Java pending transactions.' }));
    }
  }, []);

  const loadPhp = useCallback(async (page = 1) => {
    const seq = ++phpSeq.current;
    setPhp((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getPendingPhp({ page, perPage: PER_PAGE, q: phpQ.current });
      if (seq !== phpSeq.current) return;
      refreshSelectedRows(setPhpSelected, 'php', data.transactions || []);
      setPhp({
        loading: false,
        error: null,
        transactions: data.transactions || [],
        total: data.total || 0,
        unfilteredTotal: data.unfiltered_total,
        page: data.page || page,
      });
    } catch (err) {
      if (seq !== phpSeq.current) return;
      setPhp((s) => ({ ...s, loading: false, error: err.message || 'Failed to load PHP pending transactions.' }));
    }
  }, []);

  const loadAwaiting = useCallback(async (page = 1, isRetry = false) => {
    const seq = ++awaitingSeq.current;
    clearTimeout(awaitingRetry.current.timer);
    if (!isRetry) awaitingRetry.current.count = 0;
    setAwaiting((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getPendingAwaiting({ page, perPage: DEPOSIT_PER_PAGE, q: awaitingQ.current });
      if (seq !== awaitingSeq.current) return;
      if (data.building) {
        if (awaitingRetry.current.count++ < SCAN_MAX_RETRIES) {
          awaitingRetry.current.timer = setTimeout(() => loadAwaiting(page, true), SCAN_RETRY_MS);
          setAwaiting((s) => ({ ...s, loading: true, meta: { building: true }, transactions: [], total: 0 }));
        } else {
          setAwaiting((s) => ({ ...s, loading: false, meta: {}, error: 'The Redis scan is taking too long. Try Refresh in a minute.' }));
        }
        return;
      }
      refreshSelectedRows(setAwaitingSelected, 'java', data.transactions || []);
      setAwaiting({
        loading: false,
        error: null,
        meta: {
          scanComplete: data.scan_complete !== false,
          capped: Boolean(data.candidates_capped),
          inFlight: data.excluded_in_flight || 0,
          completed: data.excluded_completed || 0,
          minAge: data.min_age_seconds,
          historyError: data.history_error || null,
          generatedAt: data.generated_at || null,
          stale: Boolean(data.stale),
        },
        transactions: data.transactions || [],
        total: data.total || 0,
        unfilteredTotal: data.unfiltered_total,
        page: data.page || page,
      });
    } catch (err) {
      if (seq !== awaitingSeq.current) return;
      setAwaiting((s) => ({
        ...s,
        loading: false,
        error: err.message || 'Failed to load transactions awaiting a Bancnet response.',
      }));
    }
  }, []);

  const loadUnpaid = useCallback(async (page = 1, isRetry = false) => {
    const seq = ++unpaidSeq.current;
    clearTimeout(unpaidRetry.current.timer);
    if (!isRetry) unpaidRetry.current.count = 0;
    setUnpaid((s) => ({ ...s, loading: true, error: null }));
    try {
      const data = await getPendingUnpaid({ page, perPage: DEPOSIT_PER_PAGE, q: unpaidQ.current });
      if (seq !== unpaidSeq.current) return;
      if (data.building) {
        if (unpaidRetry.current.count++ < SCAN_MAX_RETRIES) {
          unpaidRetry.current.timer = setTimeout(() => loadUnpaid(page, true), SCAN_RETRY_MS);
          setUnpaid((s) => ({ ...s, loading: true, meta: { building: true }, transactions: [], total: 0 }));
        } else {
          setUnpaid((s) => ({ ...s, loading: false, meta: {}, error: 'The Redis scan is taking too long. Try Refresh in a minute.' }));
        }
        return;
      }
      refreshSelectedRows(setUnpaidSelected, 'java', data.transactions || []);
      setUnpaid({
        loading: false,
        error: null,
        meta: {
          scanComplete: data.scan_complete !== false,
          minAgeSeconds: data.min_age_seconds,
          tooRecent: data.too_recent || 0,
          maxAgeSeconds: data.max_age_seconds,
          tooOld: data.too_old || 0,
          generatedAt: data.generated_at || null,
          stale: Boolean(data.stale),
        },
        transactions: data.transactions || [],
        total: data.total || 0,
        unfilteredTotal: data.unfiltered_total,
        page: data.page || page,
      });
    } catch (err) {
      if (seq !== unpaidSeq.current) return;
      setUnpaid((s) => ({ ...s, loading: false, error: err.message || 'Failed to load unpaid deposits.' }));
    }
  }, []);

  function searchJava(q) {
    javaQ.current = q;
    setJavaQuery(q);
    loadJava(1);
  }

  function searchPhp(q) {
    phpQ.current = q;
    setPhpQuery(q);
    loadPhp(1);
  }

  function searchAwaiting(q) {
    awaitingQ.current = q;
    setAwaitingQuery(q);
    loadAwaiting(1);
  }

  function searchUnpaid(q) {
    unpaidQ.current = q;
    setUnpaidQuery(q);
    loadUnpaid(1);
  }

  useEffect(() => {
    loadUnpaid(1);
    loadAwaiting(1);
    loadJava(1);
    loadPhp(1);
    return () => {
      clearTimeout(awaitingRetry.current.timer);
      clearTimeout(unpaidRetry.current.timer);
    };
  }, [loadUnpaid, loadAwaiting, loadJava, loadPhp]);

  const javaTotalPages = Math.max(Math.ceil(java.total / PER_PAGE), 1);
  const phpTotalPages = Math.max(Math.ceil(php.total / PER_PAGE), 1);
  const awaitingTotalPages = Math.max(Math.ceil(awaiting.total / DEPOSIT_PER_PAGE), 1);
  const unpaidTotalPages = Math.max(Math.ceil(unpaid.total / DEPOSIT_PER_PAGE), 1);
  // Deposit lists whose rows come from the router's records and go to Java
  // through this service rather than the router (see BulkPendingStatusModal's
  // awaitingModalRecord and the two deposit endpoints).
  const isDepositList = (list) => list === 'awaiting' || list === 'unpaid';
  const LIST_LABEL = { awaiting: 'Awaiting Bancnet response', unpaid: 'Deposit with no Leg 3' };
  const LIST_SUBMIT = { awaiting: updateAwaitingDepositStatus, unpaid: rejectUnpaidDeposit };

  const rowsFor = (list) =>
    list === 'awaiting' ? awaiting.transactions : list === 'unpaid' ? unpaid.transactions
      : list === 'java' ? java.transactions : php.transactions;
  const apiSourceFor = (list) => (list === 'php' ? 'php' : 'java');
  const keySourceFor = (list) => (list === 'php' ? 'php' : 'java');

  function openModal(list, record) {
    setModal({
      show: true,
      list,
      source: apiSourceFor(list),
      record: isDepositList(list) ? awaitingModalRecord(record) : record,
    });
  }

  function closeModal() {
    setModal({ show: false, source: null, list: null, record: null });
  }

  const setSelectedFor = (list) =>
    list === 'awaiting' ? setAwaitingSelected : list === 'unpaid' ? setUnpaidSelected
      : list === 'java' ? setJavaSelected : setPhpSelected;

  function toggleRow(source, key, row) {
    if (!key) {
      return;
    }
    setSelectedFor(source)((prev) => {
      const next = new Map(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.set(key, row);
      }
      return next;
    });
  }

  // All-or-nothing over the rows that are actually updatable on THIS page -
  // a row missing its identifier can't be submitted, so including it in
  // "select all" would only guarantee a failure row in the results list.
  // Rows ticked on other pages are left as they are.
  function toggleAll(list) {
    const keySource = keySourceFor(list);
    const rows = rowsFor(list).filter((r) => isPendingRowUpdatable(keySource, r));
    setSelectedFor(list)((prev) => {
      const next = new Map(prev);
      const allOn = rows.length > 0 && rows.every((r) => prev.has(pendingRowKey(keySource, r)));
      for (const r of rows) {
        const k = pendingRowKey(keySource, r);
        if (allOn) {
          next.delete(k);
        } else {
          next.set(k, r);
        }
      }
      return next;
    });
  }

  function clearSelection(source) {
    setSelectedFor(source)(new Map());
  }

  // Refresh drops the selection: rows on other pages may have been resolved
  // since they were ticked, and Refresh is the operator asking for fresh data.
  function refreshList(list) {
    clearSelection(list);
    if (list === 'awaiting') loadAwaiting(awaiting.page);
    else if (list === 'unpaid') loadUnpaid(unpaid.page);
    else if (list === 'java') loadJava(java.page);
    else loadPhp(php.page);
  }

  function openBulkModal(list) {
    const selected = list === 'awaiting' ? awaitingSelected : list === 'unpaid' ? unpaidSelected
      : list === 'java' ? javaSelected : phpSelected;
    const keySource = keySourceFor(list);
    // Includes rows ticked on other pages. A row also on the current page is
    // taken from current state (same instance the table holds, so it can be
    // removed on success); others use the copy saved when it was ticked.
    const onPage = new Map(rowsFor(list).map((r) => [pendingRowKey(keySource, r), r]));
    const rows = Array.from(selected, ([k, r]) => onPage.get(k) || r).filter(Boolean);
    if (rows.length === 0) {
      return;
    }
    const records = isDepositList(list) ? rows.map(awaitingModalRecord) : rows;
    setBulkModal({ show: true, source: apiSourceFor(list), list, records });
  }

  function closeBulkModal() {
    setBulkModal({ show: false, source: null, list: null, records: [] });
  }

  // Awaiting rows are NOT removed on success: the transaction is still
  // stuck in Redis until instapay-router processes the status and writes
  // its callback record, so the row stays, marked as already sent - which
  // also stops a second operator sending it again. The modals hand back a
  // flattened copy (awaitingModalRecord), so this matches by ID.
  function markDepositSent(list, record, status) {
    const id = record?.merchant_order_id;
    if (!id) {
      return;
    }
    (list === 'unpaid' ? setUnpaid : setAwaiting)((s) => ({
      ...s,
      transactions: s.transactions.map((r) =>
        r.merchant_order_id === id
          ? { ...r, manual_update: { status: status || 'sent', updated_at: new Date().toLocaleString() } }
          : r
      ),
    }));
    setSelectedFor(list)((prev) => {
      if (!prev.has(id)) {
        return prev;
      }
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  }

  // Filtered by reference equality (r !== record) rather than any specific
  // ID field - deliberate, since Java Pending's identifier field name is
  // itself only a flagged guess (see PendingStatusModal's javaIdentifier)
  // and this doesn't need to know or trust it: `record` here is the exact
  // same object instance still sitting in `transactions`, so identity
  // comparison works regardless of what fields it does or doesn't have.
  // The bulk path passes the same instances straight back through, so it
  // gets the same treatment for free.
  function handleUpdateSuccess(source, record, status) {
    if (isDepositList(source)) {
      markDepositSent(source, record, status);
      return;
    }
    const key = pendingRowKey(source, record);
    if (source === 'java') {
      setJava((s) => ({
        ...s,
        transactions: s.transactions.filter((r) => r !== record && !(key && pendingRowKey(source, r) === key)),
        total: Math.max(s.total - 1, 0),
      }));
    } else {
      setPhp((s) => ({
        ...s,
        transactions: s.transactions.filter((r) => r !== record && !(key && pendingRowKey(source, r) === key)),
        total: Math.max(s.total - 1, 0),
      }));
    }
    // The row is gone from the table, so its key must go too - otherwise
    // the selection count keeps counting rows that no longer exist.
    if (key) {
      setSelectedFor(source)((prev) => {
        if (!prev.has(key)) {
          return prev;
        }
        const next = new Map(prev);
        next.delete(key);
        return next;
      });
    }
  }

  return (
    <div>
      <h1 className="h4 mb-2">Transaction Pending</h1>

      <div className="row g-3">
        <PendingCard
          colClass="col-12"
          title="Awaiting Bancnet Response"
          hint="Deposits Bancnet validated at Leg 3 (ACTC) whose final result (Leg 5) never reached the router, so Java was never told. Check Bancnet's records, then send the status to Java. Listed only while the router still holds them in Redis."
          loading={awaiting.loading}
          error={awaiting.error}
          meta={null}
          notice={<><ScanStatus meta={awaiting.meta} />{!awaiting.meta?.building && <AwaitingNotice meta={awaiting.meta} />}</>}
          records={awaiting.transactions}
          bulkBar={
            <BulkBar
              selectedCount={awaitingSelected.size}
              onBulkUpdate={() => openBulkModal('awaiting')}
              onClearSelection={() => clearSelection('awaiting')}
            />
          }
          table={
            <AwaitingTable
              records={awaiting.transactions}
              onRowClick={(row) => openModal('awaiting', row)}
              selected={awaitingSelected}
              onToggleRow={(key, row) => toggleRow('awaiting', key, row)}
              onToggleAll={() => toggleAll('awaiting')}
            />
          }
          onRefresh={() => refreshList('awaiting')}
          exportButton={
            <ExportCsvButton
              fetchPage={(page, perPage) => getPendingAwaiting({ page, perPage, q: awaitingQ.current })}
              columns={AWAITING_CSV_COLUMNS}
              keySource="java"
              fileSlug="awaiting-bancnet"
              query={awaitingQuery}
              disabled={awaiting.loading || awaiting.total === 0}
            />
          }
          search={<CardSearch value={awaitingQuery} onChange={searchAwaiting} placeholder="Search order ID, instruction ID, merchant, amount" />}
          searchActive={Boolean(awaitingQuery.trim())}
          unfilteredTotal={awaiting.unfilteredTotal}
          page={awaiting.page}
          total={awaiting.total}
          totalPages={awaitingTotalPages}
          onPrevPage={() => loadAwaiting(Math.max(awaiting.page - 1, 1))}
          onNextPage={() => loadAwaiting(Math.min(awaiting.page + 1, awaitingTotalPages))}
          onGoToPage={(p) => loadAwaiting(p)}
        />
        <PendingCard
          colClass="col-12"
          title="Deposits With No Leg 3"
          hint="Orders Java pushed where no payment validation (Leg 3) reached the router: the customer has not paid, or the Leg 3 request was lost (network flapping) and Bancnet tagged it DS24. After confirming in Bancnet, send Java the rejection."
          loading={unpaid.loading}
          error={unpaid.error}
          meta={null}
          notice={<><ScanStatus meta={unpaid.meta} />{!unpaid.meta?.building && <UnpaidNotice meta={unpaid.meta} />}</>}
          records={unpaid.transactions}
          bulkBar={
            <BulkBar
              selectedCount={unpaidSelected.size}
              onBulkUpdate={() => openBulkModal('unpaid')}
              onClearSelection={() => clearSelection('unpaid')}
            />
          }
          table={
            <UnpaidTable
              records={unpaid.transactions}
              onRowClick={(row) => openModal('unpaid', row)}
              selected={unpaidSelected}
              onToggleRow={(key, row) => toggleRow('unpaid', key, row)}
              onToggleAll={() => toggleAll('unpaid')}
            />
          }
          onRefresh={() => refreshList('unpaid')}
          exportButton={
            <ExportCsvButton
              fetchPage={(page, perPage) => getPendingUnpaid({ page, perPage, q: unpaidQ.current })}
              columns={UNPAID_CSV_COLUMNS}
              keySource="java"
              fileSlug="no-leg3"
              query={unpaidQuery}
              disabled={unpaid.loading || unpaid.total === 0}
            />
          }
          search={<CardSearch value={unpaidQuery} onChange={searchUnpaid} placeholder="Search order ID, merchant, amount" />}
          searchActive={Boolean(unpaidQuery.trim())}
          unfilteredTotal={unpaid.unfilteredTotal}
          page={unpaid.page}
          total={unpaid.total}
          totalPages={unpaidTotalPages}
          onPrevPage={() => loadUnpaid(Math.max(unpaid.page - 1, 1))}
          onNextPage={() => loadUnpaid(Math.min(unpaid.page + 1, unpaidTotalPages))}
          onGoToPage={(p) => loadUnpaid(p)}
        />
        <PendingCard
          title="Java Pending Transaction"
          hint="Transactions submitted by the Java side of the pipeline with no final status yet."
          loading={java.loading}
          error={java.error}
          meta={java.meta}
          records={java.transactions}
          bulkBar={
            <BulkBar
              selectedCount={javaSelected.size}
              onBulkUpdate={() => openBulkModal('java')}
              onClearSelection={() => clearSelection('java')}
            />
          }
          table={
            <DynamicTable
              records={java.transactions}
              onRowClick={(row) => openModal('java', row)}
              selected={javaSelected}
              onToggleRow={(key, row) => toggleRow('java', key, row)}
              onToggleAll={() => toggleAll('java')}
            />
          }
          onRefresh={() => refreshList('java')}
          exportButton={
            <ExportCsvButton
              fetchPage={(page, perPage) => getPendingJava({ page, perPage, q: javaQ.current })}
              columns={javaCsvColumns}
              keySource="java"
              fileSlug="java"
              query={javaQuery}
              disabled={java.loading || java.total === 0}
            />
          }
          search={<CardSearch value={javaQuery} onChange={searchJava} placeholder="Search order ID, instruction ID, merchant, status" />}
          searchActive={Boolean(javaQuery.trim())}
          unfilteredTotal={java.unfilteredTotal}
          page={java.page}
          total={java.total}
          totalPages={javaTotalPages}
          onPrevPage={() => loadJava(Math.max(java.page - 1, 1))}
          onNextPage={() => loadJava(Math.min(java.page + 1, javaTotalPages))}
          onGoToPage={(p) => loadJava(p)}
        />
        <PendingCard
          title="PHP Pending Transaction"
          hint="ibft_sender_transactions rows with status = PENDING, read directly from the IBFT MySQL database."
          loading={php.loading}
          error={php.error}
          meta={null}
          records={php.transactions}
          bulkBar={
            <BulkBar
              selectedCount={phpSelected.size}
              onBulkUpdate={() => openBulkModal('php')}
              onClearSelection={() => clearSelection('php')}
            />
          }
          table={
            <PhpPendingTable
              records={php.transactions}
              onRowClick={(row) => openModal('php', row)}
              selected={phpSelected}
              onToggleRow={(key, row) => toggleRow('php', key, row)}
              onToggleAll={() => toggleAll('php')}
            />
          }
          onRefresh={() => refreshList('php')}
          exportButton={
            <ExportCsvButton
              fetchPage={(page, perPage) => getPendingPhp({ page, perPage, q: phpQ.current })}
              columns={PHP_CSV_COLUMNS}
              keySource="php"
              fileSlug="php"
              query={phpQuery}
              disabled={php.loading || php.total === 0}
            />
          }
          search={<CardSearch value={phpQuery} onChange={searchPhp} placeholder="Search reference no or client reference no" />}
          searchActive={Boolean(phpQuery.trim())}
          unfilteredTotal={php.unfilteredTotal}
          page={php.page}
          total={php.total}
          totalPages={phpTotalPages}
          onPrevPage={() => loadPhp(Math.max(php.page - 1, 1))}
          onNextPage={() => loadPhp(Math.min(php.page + 1, phpTotalPages))}
          onGoToPage={(p) => loadPhp(p)}
        />
      </div>

      <PendingStatusModal
        show={modal.show}
        source={modal.source}
        record={modal.record}
        label={LIST_LABEL[modal.list]}
        submitJava={LIST_SUBMIT[modal.list]}
        rejectOnly={modal.list === 'unpaid'}
        paidOnly={paidOnlyFor(modal.list, [modal.record])}
        onClose={closeModal}
        onSuccess={(record, status) => handleUpdateSuccess(modal.list, record, status)}
      />

      <BulkPendingStatusModal
        show={bulkModal.show}
        source={bulkModal.source}
        records={bulkModal.records}
        label={LIST_LABEL[bulkModal.list]}
        submitJava={LIST_SUBMIT[bulkModal.list]}
        rejectOnly={bulkModal.list === 'unpaid'}
        paidOnly={paidOnlyFor(bulkModal.list, bulkModal.records)}
        onClose={closeBulkModal}
        onSuccess={(record, status) => handleUpdateSuccess(bulkModal.list, record, status)}
      />
    </div>
  );
}
